import { parse, serialize } from '../../utils/kql/kql';

/**
 * The KQL a query block actually runs.
 *
 * A block on a RECORD layout points back at the record being printed through
 * a `linkElement` — the lookup on the target card that names this record — so
 * the condition is composed here rather than stored. That is why KQL needed
 * no `this` keyword: the id is known at render time, and the grammar already
 * reads a bare hex id as a value.
 *
 * Composed text is PARSED before it is sent, which both validates it and
 * canonicalises it; a block whose own query is unreadable runs its link
 * condition alone rather than failing the whole sheet, because a face sheet
 * missing one panel is better than a face sheet that will not print.
 */
export function composeBlockQuery({ blockQuery = '', linkElement = '', itemId = '' } = {}) {
	const own = String(blockQuery || '').trim();
	const link = String(linkElement || '').trim();
	const id = String(itemId || '').trim();

	const parts = [];
	if (link && id) parts.push(`${link} = ${id}`);
	// Parenthesised, so a block query built with `or` cannot swallow the link
	// condition: `resident = X and (a or b)`, never `resident = X and a or b`.
	if (own) parts.push(parts.length ? `(${own})` : own);
	if (!parts.length) return '';

	const text = parts.join(' and ');
	try {
		return serialize(parse(text));
	} catch (e) {
		// The block's own query is the only part that can be malformed — the
		// link condition is built here from an id.
		if (parts.length > 1) {
			try { return serialize(parse(parts[0])); } catch (inner) { return ''; }
		}
		return '';
	}
}

/**
 * Does this block have anything to run? A block on a record layout with a
 * link but no record yet (the editor's empty canvas) has not.
 */
export const blockHasQuery = (block, itemId) => Boolean(
	composeBlockQuery({ blockQuery: block?.query, linkElement: block?.linkElement, itemId })
);
